import { all, put, takeEvery } from "@redux-saga/core/effects";
import {
  getAverageDurationOfClosedComplaints,
  getComplaint,
  getComplaintsSummary,
  getComplaintsSummaryCount,
  getComplaintsTotalCount,
  updateCompliant
} from "../../Services/database";
import { uploadCompliantProof } from "../../Services/storage";
import { isValidArray, isValidObject } from "../../Services/validators";
import { categoryMapping } from "../../Utils/Constants/Complaints";
import {
  getDuration,
  getLastSixMonthsTimestamp,
  getUniqueDataFromObj
} from "../../Utils/Constants/Constants";
import { setErrorStatus, setSuccessStatus } from "../status/action";
import store from "../store";
import { setSelectedTableData } from "../table/action";
import { setSelectedComplaint, verifyCompliant } from "./action";

export const actionTypes = {
  PUT_COMPLAINTS_DATA: "PUT_COMPLAINTS_DATA",
  GET_ALL_COMPLAINTS_COUNTS: "GET_ALL_COMPLAINTS_COUNTS",
  PUT_COMPLAINTS_EXPORT_LOADING: "PUT_COMPLAINTS_EXPORT_LOADING",
  VERIFY_COMPLAINT: "VERIFY_COMPLAINT",
  GET_COMPLAINTS_ASSETS: "GET_COMPLAINTS_ASSETS",
  GET_TOTAL_VALUES: "GET_TOTAL_VALUES",
  FIX_COMPLAINTS: "FIX_COMPLAINTS",
  COMPLAINT_TAKEOVER: "COMPLAINT_TAKEOVER",
  SET_SELECTED_COMPLAINT: "SET_SELECTED_COMPLAINT",
  GET_TODAY_COMPLAINT_COUNT: "GET_TODAY_COMPLAINT_COUNT"
};

function* getTodayComplaintCountWorker() {
  try {
    yield setSummaryLoading(true);
    const accessToken = store.getState().auth.data.accessToken;

    const todayFilter = {
      from: +new Date(`${new Date().toISOString().split("T")[0]}:00:00:00`),
      to: +new Date(`${new Date().toISOString().split("T")[0]}:23:59:59`)
    };
    const issuedByTypes = [
      "customerSupport",
      "public",
      "procedure",
      "internal",
      "independentEngineer"
    ];
    const todayBaseFilter = [
      "createdAt",
      ">=",
      todayFilter.from,
      "createdAt",
      "<=",
      todayFilter.to
    ];

    const todayComplaintPromises = [
      getComplaintsSummaryCount(todayBaseFilter, accessToken),
      ...issuedByTypes.map((type) =>
        getComplaintsSummaryCount(
          [...todayBaseFilter, "issuedBy.type", "==", type],
          accessToken
        )
      )
    ];

    const [
      todayTotalCount,
      todayCustomerSupportCount,
      todayPublicCount,
      todayProcedureCount,
      todayInternalCount,
      independentEngineerCount
    ] = yield Promise.all(todayComplaintPromises);

    yield put({
      type: "SET_TODAY_COMPLAINT_COUNT_DATA",
      payload: {
        count: {
          total: todayTotalCount,
          customerSupport: todayCustomerSupportCount,
          public: todayPublicCount,
          internal: todayProcedureCount + todayInternalCount,
          independentEngineer: independentEngineerCount
        }
      }
    });

    yield setSummaryLoading(false);
  } catch (error) {
    yield setSummaryLoading(false);
    setErrorStatus(error);
    console.error(error, "getAllComplaintsCountWorker");
  }
}

function* getAllComplaintsCountWorker(action) {
  try {
    yield setSummaryLoading(true);
    const accessToken = store.getState().auth.data.accessToken;

    const baseFilter = [...action.payload.filter];
    let updatedFilter = baseFilter.filter(
      (value, index) =>
        value !== "status.currentStatus" &&
        baseFilter[index - 1] !== "status.currentStatus" &&
        baseFilter[index - 2] !== "status.currentStatus"
    );

    const currentStatusIndex = baseFilter.includes("status.currentStatus")
      ? baseFilter.indexOf("status.currentStatus")
      : false;

    const summaryPromises = [
      getComplaintsSummaryCount(baseFilter, accessToken),
      getComplaintsSummaryCount(
        [...baseFilter, "issuedBy.type", "==", "customerSupport"],
        accessToken
      ),
      getComplaintsSummaryCount(
        [...baseFilter, "issuedBy.type", "==", "public"],
        accessToken
      ),
      getComplaintsSummaryCount(
        [...baseFilter, "issuedBy.type", "==", "independentEngineer"],
        accessToken
      ),
      getComplaintsSummaryCount(
        [...baseFilter, "issuedBy.type", "==", "procedure"],
        accessToken
      ),
      getComplaintsSummaryCount(
        [...baseFilter, "issuedBy.type", "==", "internal"],
        accessToken
      ),
      getComplaintsSummaryCount(
        [...updatedFilter, "status.currentStatus", "==", "OPEN"],
        accessToken
      ),
      getComplaintsSummaryCount(
        [...updatedFilter, "status.currentStatus", "==", "UNDER REVIEW"],
        accessToken
      ),
      getComplaintsSummaryCount(
        [...updatedFilter, "status.currentStatus", "==", "CLOSED"],
        accessToken
      ),
      getComplaintsSummaryCount(
        [...updatedFilter, "status.currentStatus", "==", "PENDING CLOSURE"],
        accessToken
      ),
      getComplaintsSummary(
        [
          ...updatedFilter,
          "status.currentStatus",
          "in",
          ["OPEN", "UNDER REVIEW"]
        ],
        { field: "createdAt", direction: "asc" },
        5
      ),
      getComplaintsSummaryCount(
        [...updatedFilter, "closedDuration", "<", 86400000],
        accessToken
      ),
      getComplaintsSummaryCount(
        [
          ...updatedFilter,
          "closedDuration",
          ">=",
          86400000,
          "closedDuration",
          "<",
          259200000
        ],
        accessToken
      ),
      getComplaintsSummaryCount(
        [
          ...updatedFilter,
          "closedDuration",
          ">=",
          259200000,
          "closedDuration",
          "<",
          604800000
        ],
        accessToken
      ),
      getComplaintsSummaryCount(
        [...updatedFilter, "closedDuration", ">=", 604800000],
        accessToken
      ),
      getAverageDurationOfClosedComplaints(
        isValidArray(updatedFilter) ? updatedFilter : null
      )
    ];

    const [
      totalCount,
      customerSupportCount,
      publicCount,
      independentEngineer,
      procedureCount,
      internalCount,
      openCount,
      reviewCount,
      closedCount,
      pendingClosureCount,
      longestDurationComplaints,
      closedBy1dayCount,
      closedBy3daysCount,
      closedBy7daysCount,
      closedByMoreThan7daysCount,
      averageDurationOfClosedComplaints
    ] = yield Promise.all(summaryPromises);

    const graphData = [
      {
        title: "COMPLAINTS SOURCE",
        count: {
          total: totalCount,
          internal: procedureCount + internalCount,
          public: publicCount,
          customerSupport: customerSupportCount,
          independentEngineer: independentEngineer
        }
      },
      {
        title: "COMPLAINTS STATUS DATA",
        count: {
          ...(currentStatusIndex
            ? baseFilter[currentStatusIndex + 2].includes("OPEN")
              ? { open: openCount }
              : {}
            : { open: openCount }),
          ...(currentStatusIndex
            ? baseFilter[currentStatusIndex + 2].includes("UNDER REVIEW")
              ? { review: reviewCount }
              : {}
            : { review: reviewCount }),
          ...(currentStatusIndex
            ? baseFilter[currentStatusIndex + 2].includes("CLOSED")
              ? { closed: closedCount }
              : {}
            : { closed: closedCount }),
          ...(currentStatusIndex
            ? baseFilter[currentStatusIndex + 2].includes("PENDING CLOSURE")
              ? { pending: pendingClosureCount }
              : {}
            : { pending: pendingClosureCount })
        }
      },
      {
        title: "COMPLAINTS CLOSURE DURATION",
        count: {
          oneDay: closedBy1dayCount,
          threeDays: closedBy3daysCount,
          sevenDays: closedBy7daysCount,
          moreThanSevenDays: closedByMoreThan7daysCount,
          average: averageDurationOfClosedComplaints
            ? Math.floor(averageDurationOfClosedComplaints / 1000 / 60 / 60)
            : 0
        }
      }
    ];

    const zoneAndCategorySummaryFilter = baseFilter.filter(
      (value, index) =>
        (value !== "location.zone" ||
          baseFilter[index - 1] !== "location.zone" ||
          baseFilter[index - 2] !== "location.zone") &&
        value
    );

    let zones = [];

    if (baseFilter.includes("location.zone")) {
      baseFilter.forEach((value, index) => {
        if (value === "location.zone") {
          zones = baseFilter[index + 2];
        }
      });
    }

    if (!isValidArray(zones)) {
      zones = getUniqueDataFromObj("zone", store.getState().locations.data);
    }

    const zoneCounts = yield Promise.all(
      zones.map(async (zone) => {
        const count = await getComplaintsSummaryCount(
          [...zoneAndCategorySummaryFilter, "location.zone", "==", zone],
          accessToken
        );
        return { [zone]: count };
      })
    ).then((results) => Object.assign({}, ...results));

    graphData.push({
      title: "ZONE WISE COMPLAINTS",
      count: zoneCounts
    });

    const lastSixMonthsTimestamp = getLastSixMonthsTimestamp();

    // remove createdAt from filter
    updatedFilter = baseFilter.filter(
      (value, index) =>
        value !== "createdAt" &&
        baseFilter[index - 1] !== "createdAt" &&
        baseFilter[index - 2] !== "createdAt"
    );
    const monthlyComparisonData = yield Promise.all(
      lastSixMonthsTimestamp.map(async (data) => {
        const openComplaintsCount = await getComplaintsSummaryCount(
          [
            ...updatedFilter,
            "status.currentStatus",
            "in",
            ["OPEN", "UNDER REVIEW"],
            "createdAt",
            ">=",
            data.start,
            "createdAt",
            "<=",
            data.end
          ],
          accessToken
        );
        const closedComplaintsCount = await getComplaintsSummaryCount(
          [
            ...updatedFilter,
            "status.currentStatus",
            "in",
            ["CLOSED"],
            "createdAt",
            ">=",
            data.start,
            "createdAt",
            "<=",
            data.end
          ],
          accessToken
        );
        return {
          month: `${data.month} ${new Date(data.end)
            .getFullYear()
            .toString()
            .slice(-2)}`,
          count: {
            open: openComplaintsCount,
            closed: closedComplaintsCount,
            total: openComplaintsCount + closedComplaintsCount
          }
        };
      })
    );

    graphData.push({
      title: "COMPLAINTS MONTHLY COMPARISON",
      count: monthlyComparisonData
    });

    const currentTime = +new Date();
    const complaintsWithDuration = longestDurationComplaints.map((data) => {
      const { totalMilliseconds, totalHours } = getDuration(
        data.createdAt,
        currentTime
      );
      return {
        ...data,
        duration: { milliseconds: totalMilliseconds, hours: totalHours }
      };
    });

    const categoriesCount = yield Promise.all(
      Object.keys(categoryMapping).map(async (category) => {
        const count = await getComplaintsSummaryCount(
          [
            ...zoneAndCategorySummaryFilter,
            "category",
            "in",
            [category, categoryMapping[category]]
          ],
          accessToken
        );
        return { [category]: count };
      })
    ).then((results) => Object.assign({}, ...results));

    graphData.push({
      title: "CATEGORY WISE COMPLAINTS",
      count: categoriesCount
    });

    graphData.push({
      title: "LONGEST DURATION COMPLAINTS",
      complaints: complaintsWithDuration
    });

    graphData.push({
      title: "MAX COMPLAINTS RAISED LOCATIONS",
      counts: []
    });

    graphData.push({
      title: "COMING SOON 1",
      counts: []
    });

    yield put({
      type: "SET_COMPLAINT_COUNT_DATA",
      payload: { data: graphData }
    });
    yield setSummaryLoading(false);
  } catch (error) {
    yield setSummaryLoading(false);
    setErrorStatus(error);
    console.error(error, "getAllComplaintsCountWorker");
  }
}

function* putComplaintsWorker(action) {
  try {
    yield setComplaintsLoading(true);
    const count = yield getComplaintsTotalCount({
      filters: action.payload.filters
    });
    yield put({
      type: "SET_COMPLAINTS_DATA",
      payload: {
        data: action.payload.data
      }
    });
    yield put({
      type: "SET_COMPLAINTS_COUNT",
      payload: {
        count: count
      }
    });

    const selectedComplaint = store.getState().table.complaints
      .selectedComplaint;

    if (isValidObject(selectedComplaint)) {
      yield setSelectedComplaint(selectedComplaint?.documentId);
    }

    yield setComplaintsLoading(false);
    yield setComplaintsPaginationLoading(false);
  } catch (error) {
    yield setComplaintsLoading(false);
    yield setComplaintsPaginationLoading(false);
    console.error(error, "putComplaintsWorker");
    setErrorStatus(error);
  }
}

function* verifyComplaintWorker(action) {
  try {
    yield setComplaintsLoading(true);
    const profileData = store.getState().profile.data;
    const complaintData = store.getState().complaints.data?.[
      action.payload.documentId
    ];

    const data = {
      status: {
        currentStatus:
          action.payload.type === "CLOSE" ? "PENDING CLOSURE" : "OPEN",
        updatedAt: +new Date()
      },
      ...(action.payload.type === "CLOSE"
        ? {
            closedBy: {
              employeeId: profileData.employeeId,
              employeeName: profileData.name,
              email: profileData.email
            }
          }
        : {}),

      latestUpdate: {
        description: `Status changed from UNDER REVIEW to ${
          action.payload.type === "CLOSE" ? "PENDING CLOSURE" : "OPEN"
        }`,
        previousStatus: "UNDER REVIEW",
        currentStatus:
          action.payload.type === "CLOSE" ? "PENDING CLOSURE" : "OPEN",
        updatedAt: +new Date(),
        updatedBy: {
          employeeId: profileData.employeeId,
          employeeName: profileData.name,
          email: profileData.email
        }
      },

      ...(complaintData?.turnaround && action.payload.type === "CLOSE"
        ? {
            turnaroundOrderBy:
              complaintData.turnaround >= +new Date()
                ? 9999999999998
                : 9999999999997
          }
        : {})
    };

    yield updateCompliant(action.payload.documentId, data);
    const updatedComplaint = yield getComplaint(action.payload.documentId);
    yield setSelectedTableData("COMPLAINTS", updatedComplaint);
    yield setSuccessStatus(
      `Complaint ${
        action.payload.type === "CLOSE" ? "closed" : "opened"
      } successfully`
    );

    yield setComplaintsLoading(false);
  } catch (error) {
    console.error("verifyComplaintWorker", error);
    yield setComplaintsLoading(false);
    setErrorStatus(error);
  }
}

function* getTotalValuesWorker(action) {
  try {
    yield setComplaintsLoading(true);

    const daysInMonth = (month, year) => {
      return new Date(year, month, 0).getDate();
    };

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const daysInCurrMonth = daysInMonth(currentMonth, currentYear);

    let positive = new Array(daysInCurrMonth).fill(0);
    let negative = new Array(daysInCurrMonth).fill(0);
    if (isValidArray(action.payload.data)) {
      action.payload.data.forEach((element) => {
        if (
          isValidObject(element.procedureCount) &&
          element.procedureCount[currentYear]?.[currentMonth]
        ) {
          const value = Object.entries(
            element.procedureCount[currentYear]?.[currentMonth]
          );
          value.forEach(([day, values]) => {
            positive[parseInt(day)] += values.positive ? values.positive : 0;
            negative[parseInt(day)] += values.negative ? values.negative : 0;
          });
        }
      });
    }

    yield put({
      type: "SET_TOTAL_PROCEDURE_COUNT",
      payload: {
        positive: positive,
        negative: negative
      }
    });

    yield setComplaintsLoading(false);
  } catch (error) {
    yield setComplaintsLoading(false);
    setErrorStatus(error);
  }
}

function* fixComplaintsWorker(action) {
  try {
    yield setComplaintsLoading(true);
    const profileData = store.getState().profile.data;
    const { proof, complaintId, locationId } = action.payload;
    let imageFileUrls = [];
    let audioFileUrls = [];

    if (isValidArray(proof?.image)) {
      const imageUploadResults = yield* uploadFiles(
        proof.image,
        complaintId,
        locationId,
        "closure"
      );

      imageFileUrls = imageUploadResults.map(
        (result) =>
          `${process.env.REACT_APP_DEFAULT_BUCKET}/${locationId}/complaints/${complaintId}/closure/${result}`
      );
    }

    if (proof.audio) {
      const audioUploadResults = yield* uploadFiles(
        [proof.audio],
        complaintId,
        locationId,
        "closure"
      );
      audioFileUrls = audioUploadResults.map(
        (result) =>
          `${process.env.REACT_APP_DEFAULT_BUCKET}/${locationId}/complaints/${complaintId}/closure/${result}`
      );
    }

    const filesUrls = [...imageFileUrls, ...audioFileUrls];

    const currentTime = +new Date();

    yield updateCompliant(complaintId, {
      status: { currentStatus: "UNDER REVIEW", updatedAt: currentTime },
      closure: {
        resolvedAt: currentTime,
        resolvedBy: profileData.employeeId,
        ...(isValidArray(filesUrls) ? { proof: filesUrls } : {}),
        ...(proof?.remarks && proof.remarks.length > 0
          ? { remarks: proof?.remarks }
          : {})
      },
      latestUpdate: {
        description:
          proof?.remarks && proof.remarks.length > 0
            ? proof.remarks
            : "Status changed from OPEN to UNDER REVIEW",
        previousStatus: "OPEN",
        currentStatus: "UNDER REVIEW",
        updatedAt: currentTime,
        ...(isValidArray(filesUrls) ? { proof: filesUrls } : {}),
        updatedBy: {
          employeeId: profileData.employeeId,
          employeeName: profileData.name,
          email: profileData.email
        }
      }
    });

    if (action.payload.verify) {
      verifyCompliant(
        store.getState().table.complaints.selectedComplaint.documentId,
        "CLOSE"
      );
    } else {
      const updatedComplaint = yield getComplaint(complaintId);
      yield setSelectedTableData("COMPLAINTS", updatedComplaint);
      yield setSuccessStatus(`Complaint closed successfully`);
    }

    yield setComplaintsLoading(false);
  } catch (error) {
    console.error("fixComplaintsWorker", error);
    setErrorStatus(error);
    yield setComplaintsLoading(false);
  }
}

function* complaintTakeoverWorker(action) {
  try {
    yield setComplaintsLoading(true);
    const profileData = store.getState().profile.data;
    const currentTime = +new Date();

    yield updateCompliant(action.payload.complaintId, {
      takenOverBy: {
        employeeId: profileData.employeeId,
        email: profileData.email,
        employeeName: profileData.name,
        takenOverAt: currentTime
      },
      latestUpdate: {
        description: `Taken over by ${profileData.email} ${profileData.name}`,
        updatedAt: currentTime,
        updatedBy: {
          employeeId: profileData.employeeId,
          employeeName: profileData.name,
          email: profileData.email
        }
      }
    });

    const updatedComplaint = yield getComplaint(action.payload.complaintId);
    yield setSelectedTableData("COMPLAINTS", updatedComplaint);
    setSuccessStatus("Compliant Taken over successfully");

    yield setComplaintsLoading(false);
  } catch (error) {
    console.error("complaintTakeoverWorker", error);
    setErrorStatus(error);
    yield setComplaintsLoading(false);
  }
}

function* setComplaintExportLoadingWorker(action) {
  yield put({
    type: "SET_COMPLAINTS_EXPORT_LOADING",
    payload: {
      loading: action.payload.loading
    }
  });
}

function* setSelectedComplaintWorker(action) {
  try {
    yield setComplaintsLoading(true);
    const complaintId = action.payload.complaintId;
    if (!complaintId) {
      setSelectedTableData("COMPLAINTS", null);
      yield setComplaintsLoading(false);
      return;
    }

    const complaintsData = store.getState().complaints.data;
    let selectedComplaint = complaintsData?.[complaintId];

    if (!isValidObject(selectedComplaint)) {
      selectedComplaint = yield getComplaint(complaintId);
      if (!isValidObject(selectedComplaint)) {
        if (action.payload.navigate) {
          action.payload.navigate("/locations/complaints");
        }
        yield setErrorStatus({
          code: "custom",
          message: "Complaint resolved or not found"
        });
        yield setComplaintsLoading(false);
        return;
      }
    }

    yield setSelectedTableData("COMPLAINTS", selectedComplaint);
    yield setComplaintsLoading(false);
  } catch (error) {
    console.error("setSelectedComplaint", error);
    setErrorStatus(error);
    yield setComplaintsLoading(false);
  }
}

export default function* complaintsWatcher() {
  yield all([
    takeEvery("PUT_COMPLAINTS_DATA", putComplaintsWorker),
    takeEvery("PUT_COMPLAINTS_EXPORT_LOADING", setComplaintExportLoadingWorker),
    takeEvery("GET_ALL_COMPLAINTS_COUNTS", getAllComplaintsCountWorker),
    takeEvery("VERIFY_COMPLAINT", verifyComplaintWorker),
    takeEvery("GET_TOTAL_VALUES", getTotalValuesWorker),
    takeEvery("FIX_COMPLAINTS", fixComplaintsWorker),
    takeEvery("COMPLAINT_TAKEOVER", complaintTakeoverWorker),
    takeEvery("SET_SELECTED_COMPLAINT", setSelectedComplaintWorker),
    takeEvery("GET_TODAY_COMPLAINT_COUNT", getTodayComplaintCountWorker)
  ]);
}

function* setComplaintsLoading(bool) {
  yield put({
    type: "SET_COMPLAINTS_LOADING",
    payload: {
      loading: bool
    }
  });
}

function* setSummaryLoading(bool) {
  yield put({
    type: "SET_SUMMARY_LOADING",
    payload: {
      loading: bool
    }
  });
}

function* setComplaintsPaginationLoading(bool) {
  yield put({
    type: "SET_COMPLAINTS_PAGINATION_LOADING",
    payload: {
      loading: bool
    }
  });
}

// function* setComplaintsAssetsLoading(bool) {
//   yield put({
//     type: "SET_COMPLAINTS_ASSETS_LOADING",
//     payload: {
//       assetsLoading: bool,
//     },
//   });
// }

function* uploadFiles(files, complaintId, locationId, folder) {
  const uploadPromises = files.map((file) =>
    uploadCompliantProof(file, complaintId, locationId, folder)
  );
  return yield Promise.all(uploadPromises);
}
